import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getFirestore, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { firebaseApp } from "../../firebase/firebaseApp";
import { useAuth } from "../../context/AuthContext";
import { useUserDoc } from "../../hooks/useUserDoc";

/**
 * SaveProgressBanner
 * - Renders a CTA for anonymous users to create an account (freemium)
 * - Handles freemium plan selection + onboarding step internally
 *
 * Props:
 *  hasAnyRecordings: boolean  // to tweak copy
 *  className?: string         // optional wrapper classes
 */
export default function SaveProgressBanner({
  hasAnyRecordings,
  className = "",
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const db = useMemo(() => getFirestore(firebaseApp), []);
  const { data } = useUserDoc();

  const isAnon = !!user?.isAnonymous;
  const onboardingStep = data?.onboarding?.step;
  const isRegistered =
    data?.accountStatus === "registered" ||
    onboardingStep === "accountCreated" ||
    onboardingStep === "paymentComplete";

  if (!isAnon) return null;

  const startFreeOnboarding = async () => {
    if (!user?.uid) {
      navigate("/auth/login", { replace: false, state: { from: "/home" } });
      return;
    }

    const freePlan = {
      tier: "free",
      interval: null,
      kind: "freemium",
      currency: "GBP",
      price: 0,
      cadenceLabel: "",
      title: "Free",
      selectedAt: serverTimestamp(),
    };

    const base = { plan: freePlan };
    const onboardingPatch = isRegistered
      ? {}
      : { onboarding: { step: "planSelected", updatedAt: serverTimestamp() } };

    await setDoc(
      doc(db, "lungform_users", user.uid),
      { ...base, ...onboardingPatch },
      { merge: true }
    );

    navigate("/auth/create-account");
  };

  return (
    <div
      className={`rounded-2xl flex flex-col gap-4 h-fit items-start ${className}`}
    >
      <h3 className="subtitle-text">
        {t("anon.saveProgressTitle", {
          defaultValue: "Don't lose your progress",
        })}
      </h3>
      <p className="text-sm text-typography/80">
        {hasAnyRecordings
          ? t("anon.saveProgressCopyWithItems", {
              defaultValue:
                "Create a free account to save your recordings, sync across devices, and continue where you left off.",
            })
          : t("anon.saveProgressCopyNoItems", {
              defaultValue:
                "Create a free account now so your future recordings are saved and available when you return.",
            })}
      </p>
      <div className="flex items-center gap-2 justify-end w-full">
        <button
          type="button"
          onClick={startFreeOnboarding}
          className="primary-button"
          aria-label={t("anon.createCta", { defaultValue: "Create account" })}
        >
          {t("anon.createCta", { defaultValue: "Create account" })}
        </button>
        <Link
          to="/auth/login"
          className="outlined-button"
          aria-label={t("anon.signInCta", { defaultValue: "Sign in" })}
        >
          {t("anon.signInCta", { defaultValue: "Sign in" })}
        </Link>
      </div>
    </div>
  );
}
